import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LOGO_SRC } from "@/config";
import { ConsumerWaitlist } from "@/components/join/ConsumerWaitlist";
import { ProducerForm } from "@/components/join/ProducerForm";
import { PricingCards } from "@/components/join/PricingCards";
import { Showcase } from "@/components/join/Showcase";
import { LegalModal } from "@/components/join/LegalModal";
import { ShoppingBasket, Tractor, ArrowLeft } from "lucide-react";

export default function Join() {
  const [mode, setMode] = useState("buyer"); // buyer | producer
  const [legal, setLegal] = useState({ open: false, kind: "terms_of_service" });
  const openLegal = (kind) => setLegal({ open: true, kind });

  // Ensure the waitlist form is in view when arriving from any CTA.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="join-page" className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F9F6F0]/90 backdrop-blur-md border-b border-[#4A6B41]/15">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-2 flex items-center justify-between">
          <Link to="/" data-testid="join-logo-link" className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="Source2Table logo" className="h-14 w-14 object-contain" />
            <span className="font-sans font-extrabold text-2xl leading-none tracking-tight">
              <span className="text-black">Source</span>
              <span className="text-[#4A6B41]">2</span>
              <span className="text-black">Table</span>
            </span>
          </Link>
          <Link to="/" data-testid="join-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2F4829] hover:text-[#4A6B41]">
            <ArrowLeft size={16} /> Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-12 pb-8 text-center">
        <span data-testid="join-beta-badge"
          className="inline-block rounded-full bg-[#C86D51]/15 text-[#b65d43] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em]">
          Private Beta
        </span>
        <p className="mt-6 font-['Lobster'] text-[#4A6B41] text-5xl sm:text-6xl">
          Where Fresh Starts
        </p>
        <h1 className="mt-4 font-sans font-extrabold text-3xl sm:text-4xl text-[#1C241B] tracking-tight">
          Join the Source2Table community
        </h1>
        <p className="mt-4 mx-auto max-w-xl text-[#5A6757]">
          Whether you grow it or love to buy it — get early access as we connect
          local producers directly with their community.
        </p>
      </section>

      {/* Mode toggle */}
      <div className="px-4 sm:px-6">
        <div className="mx-auto max-w-md grid grid-cols-2 gap-2 p-1.5 rounded-full bg-white border border-[#D8E0D5] shadow-sm">
          <button data-testid="join-mode-buyer" onClick={() => setMode("buyer")}
            className={`inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors ${
              mode === "buyer" ? "bg-[#4A6B41] text-[#F9F6F0]" : "text-[#2F4829]"}`}>
            <ShoppingBasket size={16} /> I'm a Buyer
          </button>
          <button data-testid="join-mode-producer" onClick={() => setMode("producer")}
            className={`inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors ${
              mode === "producer" ? "bg-[#4A6B41] text-[#F9F6F0]" : "text-[#2F4829]"}`}>
            <Tractor size={16} /> I'm a Producer
          </button>
        </div>
      </div>

      {/* Panels */}
      <section className="px-4 sm:px-6 py-10">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white border border-[#D8E0D5] shadow-sm p-6 sm:p-10">
          {mode === "buyer" ? (
            <div>
              <h2 className="font-sans font-extrabold text-2xl text-[#1C241B]">
                Join the consumer waitlist
              </h2>
              <p className="mt-2 text-[#5A6757]">
                Be first to shop fresh, local and direct when we launch near you.
              </p>
              <div className="mt-6 max-w-md">
                <ConsumerWaitlist onOpenLegal={openLegal} />
              </div>
              <div className="mt-12">
                <h3 className="font-sans font-extrabold text-xl text-[#1C241B]">
                  Membership pricing
                </h3>
                <p className="mt-1 text-sm text-[#5A6757]">
                  Launch pricing for buyers once we go live.
                </p>
                <div className="mt-5">
                  <PricingCards />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="font-sans font-extrabold text-2xl text-[#1C241B]">
                Register as a producer
              </h2>
              <p className="mt-2 text-[#5A6757]">
                Tell us about your farm or business. Our team reviews every
                application before approval.
              </p>
              <div className="mt-6">
                <ProducerForm onOpenLegal={openLegal} />
              </div>
            </div>
          )}
        </div>

        <p className="mx-auto max-w-3xl mt-6 text-center text-xs text-[#5A6757]">
          By joining you agree to Source2Table's{" "}
          <button data-testid="footer-terms-link" onClick={() => openLegal("terms_of_service")}
            className="underline text-[#4A6B41] font-semibold">Terms of Service</button>,{" "}
          <button data-testid="footer-privacy-link" onClick={() => openLegal("privacy_policy")}
            className="underline text-[#4A6B41] font-semibold">Privacy Policy</button>{" "}
          and{" "}
          <button data-testid="footer-disclaimer-link" onClick={() => openLegal("marketplace_disclaimer")}
            className="underline text-[#4A6B41] font-semibold">Marketplace Disclaimer</button>.
        </p>
      </section>

      <Showcase />

      <LegalModal open={legal.open} initialKind={legal.kind}
        onClose={() => setLegal((s) => ({ ...s, open: false }))} />
    </div>
  );
}
